import { useMemo } from 'react'
import { naturalSortComparator } from '@editorUtils'
import { useHideElements } from '@editor/hooks'
import { useAppStore } from '@stores'
import type { ElementListFilters } from '../components/ListFilterButton'
import type { Results2ListItem } from '../types'

type UseElementListFiltersProps = {
  items: Results2ListItem[]
  /** Tree mode keeps parents of matching children visible and hides children with their group in 3D. */
  isTree: boolean
  /** When the comment column is hidden, comments are excluded from text search. */
  showCommentColumn: boolean
  /** Text search — owned by the parent view state so it survives form remounts. */
  filterText: string
  /** Filter popover state — owned by the parent view state so it survives form remounts. */
  filters: ElementListFilters
}

type UseElementListFiltersResult = {
  onlyWarn: boolean
  setOnlyWarn: (next: boolean) => void
  filter3d: boolean
  setFilter3d: (next: boolean) => void
  /** Items passing all active filters, in original `items` order. */
  visibleItems: Results2ListItem[]
  crossSectionOptions: string[]
  warnCount: number
}

/**
 * Derives the visible items from the (parent-owned) text/popover filters plus the store-backed
 * "Nur Warnungen" toggle. Also mirrors the active filters into the 3D scene ("Filter in 3D anw.")
 * by hiding elements that are filtered out of the list.
 */
export const useElementListFilters = ({
  items,
  isTree,
  showCommentColumn,
  filterText,
  filters,
}: UseElementListFiltersProps): UseElementListFiltersResult => {
  const onlyWarn = useAppStore(state => state.results2OnlyWarnings)
  const setOnlyWarn = useAppStore(state => state.setResults2OnlyWarnings)
  const filter3d = useAppStore(state => state.results2Filter3d)
  const setFilter3d = useAppStore(state => state.setResults2Filter3d)

  const visibleItems = useMemo(() => {
    const q = filterText.trim().toLowerCase()
    const labelQuery = filters.label.trim().toLowerCase()
    const crossSectionSet = filters.crossSections.length > 0 ? new Set(filters.crossSections) : null
    const exportedFilter = filters.exported
    const positionFilter = filters.shearAnchorPosition
    const keep = new Set<string>()
    const parentCommentById: Record<string, string> = {}
    const parentExtendedById: Record<string, string> = {}
    for (const item of items) {
      if (!item.parent) {
        parentCommentById[item.id] = item.comment ?? ''
        parentExtendedById[item.id] = item.extended_comment ?? ''
      }
    }
    for (const r of items) {
      const matchWarn = !onlyWarn || r.status === 'warn' || r.status === 'err'
      const commentForFilter =
        (r.comment ?? '') || (r.parent ? parentCommentById[r.parent] ?? '' : '')
      const extendedForFilter =
        (r.extended_comment ?? '') || (r.parent ? parentExtendedById[r.parent] ?? '' : '')
      const matchComment = showCommentColumn && commentForFilter.toLowerCase().includes(q)
      const matchExtendedComment = showCommentColumn && extendedForFilter.toLowerCase().includes(q)
      const matchQuery =
        !q ||
        r.id.toLowerCase().includes(q) ||
        (r.label ?? '').toLowerCase().includes(q) ||
        r.qs.toLowerCase().includes(q) ||
        matchComment ||
        matchExtendedComment
      const matchLabel =
        !labelQuery ||
        (r.label ?? '').toLowerCase().includes(labelQuery) ||
        r.id.toLowerCase().includes(labelQuery)
      const matchCrossSection = !crossSectionSet || crossSectionSet.has(r.qs)
      const matchExported =
        exportedFilter === 'all' || (exportedFilter === 'exported' ? !!r.ok : !r.ok)
      const matchPosition = positionFilter === 'all' || r.shearAnchorPosition === positionFilter
      if (
        matchWarn &&
        matchQuery &&
        matchLabel &&
        matchCrossSection &&
        matchExported &&
        matchPosition
      ) {
        keep.add(r.id)
        if (isTree && r.parent) keep.add(r.parent)
      }
    }
    return items.filter(i => keep.has(i.id))
  }, [items, onlyWarn, filterText, filters, showCommentColumn, isTree])

  const crossSectionOptions = useMemo(() => {
    const set = new Set<string>()
    for (const it of items) {
      if (it.qs?.trim()) set.add(it.qs)
    }
    return Array.from(set).sort((a, b) => naturalSortComparator(a, b))
  }, [items])

  const warnCount = useMemo(
    () => items.filter(i => i.status === 'warn' || i.status === 'err').length,
    [items],
  )

  // Apply list filter to 3D scene by hiding elements that are currently not visible in the list.
  // Mirrors the behavior of the legacy Results grid ("Filter in 3D anw.").
  const filteredHiddenIds = useMemo(() => {
    if (!filter3d) return null

    const visibleIds = new Set(visibleItems.map(i => i.id))

    if (!isTree) {
      return items.filter(i => !visibleIds.has(i.id)).map(i => i.id)
    }

    // Tree mode: only hide a child if its parent group is also invisible.
    const childToParent = new Map<string, string>()
    for (const item of items) {
      if (item.parent) childToParent.set(item.id, item.parent)
    }

    const nextHiddenIds: string[] = []
    for (const item of items) {
      const parentId = childToParent.get(item.id)
      if (!visibleIds.has(parentId ?? item.id)) nextHiddenIds.push(item.id)
    }

    return nextHiddenIds
  }, [filter3d, isTree, items, visibleItems])

  useHideElements(filteredHiddenIds)

  return {
    onlyWarn,
    setOnlyWarn,
    filter3d,
    setFilter3d,
    visibleItems,
    crossSectionOptions,
    warnCount,
  }
}
